/**
 * Canvas Placement Engine — Phase 2
 * Translates semantic placement intents into collision-free pixel coordinates.
 */

export type PlacementStrategy =
  | { type: 'center_viewport' }
  | { type: 'relative_to_item'; targetItemId: string; relation: 'top' | 'bottom' | 'left' | 'right' }
  | { type: 'empty_space'; preferredRegion?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center' }
  | { type: 'absolute'; x: number; y: number }
  | { type: 'grid'; columns?: number; startX?: number; startY?: number }
  | { type: 'cluster'; centerItemId?: string; radius?: number };

export interface PlacementResult {
  x: number;
  y: number;
}

export interface PlacementContext {
  existingItems: Array<{ id: string; x: number; y: number; width: number; height: number }>;
  viewportBounds: { left: number; top: number; right: number; bottom: number };
  itemToPlace: { width: number; height: number };
}

/**
 * Main placement function — compute collision-free coordinates for a new item.
 */
export function calculatePlacement(
  strategy: PlacementStrategy,
  context: PlacementContext
): PlacementResult {
  let position: PlacementResult;

  switch (strategy.type) {
    case 'center_viewport':
      position = placeCenterViewport(context);
      break;
    case 'relative_to_item':
      position = placeRelativeToItem(strategy.targetItemId, strategy.relation, context);
      break;
    case 'empty_space':
      position = placeInEmptySpace(context, strategy.preferredRegion);
      break;
    case 'absolute':
      position = { x: strategy.x, y: strategy.y };
      break;
    default:
      position = placeCenterViewport(context);
  }

  return ensureNoCollision(position, context);
}

function placeCenterViewport(context: PlacementContext): PlacementResult {
  const { viewportBounds, itemToPlace } = context;
  return {
    x: (viewportBounds.left + viewportBounds.right) / 2 - itemToPlace.width / 2,
    y: (viewportBounds.top + viewportBounds.bottom) / 2 - itemToPlace.height / 2,
  };
}

function placeRelativeToItem(
  targetItemId: string,
  relation: 'top' | 'bottom' | 'left' | 'right',
  context: PlacementContext
): PlacementResult {
  const target = context.existingItems.find(i => i.id === targetItemId);
  if (!target) return placeCenterViewport(context);

  const GAP = 40;
  const { width, height } = context.itemToPlace;

  switch (relation) {
    case 'top':
      return { x: target.x + (target.width - width) / 2, y: target.y - height - GAP };
    case 'bottom':
      return { x: target.x + (target.width - width) / 2, y: target.y + target.height + GAP };
    case 'left':
      return { x: target.x - width - GAP, y: target.y + (target.height - height) / 2 };
    case 'right':
      return { x: target.x + target.width + GAP, y: target.y + (target.height - height) / 2 };
    default:
      return { x: target.x + target.width + GAP, y: target.y };
  }
}

function placeInEmptySpace(
  context: PlacementContext,
  preferredRegion?: string
): PlacementResult {
  const { existingItems, viewportBounds, itemToPlace } = context;
  const { width, height } = itemToPlace;
  const GAP = 30;

  if (existingItems.length === 0) return placeCenterViewport(context);

  const allBounds = {
    minX: Math.min(...existingItems.map(i => i.x)),
    maxX: Math.max(...existingItems.map(i => i.x + i.width)),
    minY: Math.min(...existingItems.map(i => i.y)),
    maxY: Math.max(...existingItems.map(i => i.y + i.height)),
  };

  const candidates: PlacementResult[] = [];

  if (preferredRegion === 'top-left' || !preferredRegion) {
    candidates.push({ x: allBounds.minX, y: allBounds.minY - height - GAP });
  }
  if (preferredRegion === 'top-right' || !preferredRegion) {
    candidates.push({ x: allBounds.maxX + GAP, y: allBounds.minY });
  }
  if (preferredRegion === 'bottom-left' || !preferredRegion) {
    candidates.push({ x: allBounds.minX, y: allBounds.maxY + GAP });
  }
  if (preferredRegion === 'bottom-right' || !preferredRegion) {
    candidates.push({ x: allBounds.maxX + GAP, y: allBounds.maxY + GAP });
  }
  if (preferredRegion === 'center' || !preferredRegion) {
    candidates.push(placeCenterViewport(context));
  }

  // Natural reading direction — right of rightmost item
  candidates.push({ x: allBounds.maxX + GAP * 2, y: allBounds.minY });

  let bestCandidate = candidates[0];
  let bestScore = -Infinity;

  for (const candidate of candidates) {
    let score = 0;

    // Prefer positions within the viewport
    if (
      candidate.x >= viewportBounds.left && candidate.x + width <= viewportBounds.right &&
      candidate.y >= viewportBounds.top && candidate.y + height <= viewportBounds.bottom
    ) {
      score += 100;
    }

    // Penalize collisions
    const collisionCount = existingItems.filter(item =>
      rectsOverlap(candidate.x, candidate.y, width, height, item.x, item.y, item.width, item.height, GAP)
    ).length;
    score -= collisionCount * 50;

    // Prefer closer to the viewport center
    const vcx = (viewportBounds.left + viewportBounds.right) / 2;
    const vcy = (viewportBounds.top + viewportBounds.bottom) / 2;
    const dist = Math.sqrt(Math.pow(candidate.x - vcx, 2) + Math.pow(candidate.y - vcy, 2));
    score -= dist * 0.01;

    if (score > bestScore) {
      bestScore = score;
      bestCandidate = candidate;
    }
  }

  return bestCandidate;
}

function rectsOverlap(
  x1: number, y1: number, w1: number, h1: number,
  x2: number, y2: number, w2: number, h2: number,
  gap: number = 0
): boolean {
  return !(
    x1 + w1 + gap <= x2 ||
    x2 + w2 + gap <= x1 ||
    y1 + h1 + gap <= y2 ||
    y2 + h2 + gap <= y1
  );
}

/**
 * If the proposed position overlaps existing items, nudge it until it doesn't.
 * Uses a spiral outward search pattern.
 */
export function ensureNoCollision(
  position: PlacementResult,
  context: PlacementContext,
  maxAttempts: number = 50
): PlacementResult {
  const { existingItems, itemToPlace } = context;
  const { width, height } = itemToPlace;
  const GAP = 20;

  const hasCollision = (px: number, py: number) =>
    existingItems.some(item =>
      rectsOverlap(px, py, width, height, item.x, item.y, item.width, item.height, GAP)
    );

  if (!hasCollision(position.x, position.y)) return position;

  const STEP = 40;
  for (let ring = 1; ring <= maxAttempts; ring++) {
    const offsets = [
      { dx: STEP * ring, dy: 0 },
      { dx: 0, dy: STEP * ring },
      { dx: -STEP * ring, dy: 0 },
      { dx: 0, dy: -STEP * ring },
      { dx: STEP * ring, dy: STEP * ring },
      { dx: -STEP * ring, dy: STEP * ring },
      { dx: STEP * ring, dy: -STEP * ring },
      { dx: -STEP * ring, dy: -STEP * ring },
    ];

    for (const offset of offsets) {
      const x = position.x + offset.dx;
      const y = position.y + offset.dy;
      if (!hasCollision(x, y)) return { x, y };
    }
  }

  // Absolute fallback
  const maxX = Math.max(...existingItems.map(i => i.x + i.width), 0);
  return { x: maxX + 100, y: position.y };
}
